import React, { useEffect, useRef } from 'react';
import './CustomPointer.css';

const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

export const CustomPointer: React.FC = () => {
  const pointerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const finePointer = window.matchMedia(FINE_POINTER_QUERY);
    const pointer = pointerRef.current;

    if (!finePointer.matches || !pointer) {
      return undefined;
    }

    let frameId = 0;
    let pointerX = window.innerWidth / 2;
    let pointerY = window.innerHeight / 2;

    const renderPointer = () => {
      const viewportWidth = Math.max(window.innerWidth, 1);
      const viewportHeight = Math.max(window.innerHeight, 1);
      const offsetX = (pointerX / viewportWidth - 0.5) * 100;
      const offsetY = (pointerY / viewportHeight - 0.5) * 100;

      pointer.style.transform = `translate3d(${offsetX.toFixed(3)}vw, ${offsetY.toFixed(3)}vh, 0)`;
      pointer.dataset.visible = 'true';
      frameId = 0;
    };

    const handlePointerMove = (event: MouseEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;

      if (frameId === 0) {
        frameId = window.requestAnimationFrame(renderPointer);
      }
    };

    const hidePointer = () => {
      pointer.dataset.visible = 'false';
    };

    const handlePointerOut = (event: MouseEvent) => {
      if (event.relatedTarget === null) {
        hidePointer();
      }
    };

    window.addEventListener('mousemove', handlePointerMove, { passive: true });
    window.addEventListener('mouseout', handlePointerOut);
    window.addEventListener('blur', hidePointer);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseout', handlePointerOut);
      window.removeEventListener('blur', hidePointer);

      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  return (
    <div className="custom-pointer-wrapper" aria-hidden="true">
      <div ref={pointerRef} className="custom-pointer" data-visible="false" />
    </div>
  );
};
